import React from 'react'
import { Variant } from 'react-bootstrap/esm/types'

interface BaseSpinnerProps {
  isVisible?: boolean
  className?: string
  style?: React.CSSProperties
  variant?: Variant
  title?: string
}

export function BaseSpinner({
  isVisible = true,
  className,
  style,
  variant,
  title = 'Loading',
}: BaseSpinnerProps): JSX.Element | null {
  if (!isVisible) {
    return null
  }

  const colorStyle: React.CSSProperties = variant
    ? { color: `var(--bs-${variant}, ${variant})`, ...style }
    : { ...style }

  return (
    <div className={`svg-spinner ${className || ''}`} style={colorStyle} title={title}>
      <svg width="24" height="24" viewBox="0 0 110 110">
        <circle className="small-circle base-form thick" cx="50%" cy="50%" r="50"></circle>
        <circle className="small-circle base-form medium" cx="50%" cy="50%" r="50"></circle>
        <circle className="small-circle base-form thin" cx="50%" cy="50%" r="50"></circle>
        <circle className="small-circle base-form thick-rev" cx="50%" cy="50%" r="50"></circle>
        <circle className="small-circle base-form medium-rev" cx="50%" cy="50%" r="50"></circle>
        <circle className="small-circle base-form thin-rev" cx="50%" cy="50%" r="50"></circle>
      </svg>
    </div>
  )
}

/**
 * Show loader as the only element on the page, possibly with message
 */
export default function PageLoader({
  message,
  style,
  messageStyle,
}: {
  message?: JSX.Element | string | undefined
  style?: { [key: string]: any } | undefined
  messageStyle?: { [key: string]: any } | undefined
}): JSX.Element {
  const messageComponent = typeof message === 'string' ? <p>{message}</p> : (message ?? null)

  return (
    <div className="page-loader" style={style}>
      <div className="spinner-container">
        <BaseSpinner style={{ transform: 'scale(1.5)' }} />
      </div>
      <div className="page-loader-message" style={messageStyle}>
        {messageComponent}
      </div>
    </div>
  )
}

export function SmallPageLoader({
  message,
  className,
  style,
}: {
  message?: string | undefined
  className?: string | undefined
  style?: { [key: string]: any } | undefined
}): JSX.Element {
  return (
    <div className={'small-page-loader ' + (className || '')} style={style}>
      <BaseSpinner />
      {message && <p className="small-page-loader-message">{message}</p>}
    </div>
  )
}

export function ReportScanSpinner({ message }: { message?: JSX.Element | string | undefined }) {
  const messageComponent = typeof message === 'string' ? <p className="text-center">{message}</p> : (message ?? null)

  return (
    <div className="report-scan-spinner">
      <div className="spinner-container">
        <i className="owl-head spinner-central-icon"></i>
        <div className="report-spinner-image">
          <svg>
            <circle className="outer-circle base-form thick" cx="50%" cy="50%" r="60"></circle>
            <circle className="outer-circle base-form medium" cx="50%" cy="50%" r="60"></circle>
            <circle className="outer-circle base-form thin" cx="50%" cy="50%" r="60"></circle>
            <circle className="outer-circle base-form thick-rev" cx="50%" cy="50%" r="60"></circle>
            <circle className="outer-circle base-form medium-rev" cx="50%" cy="50%" r="60"></circle>
            <circle className="outer-circle base-form thin-rev" cx="50%" cy="50%" r="60"></circle>
            <circle className="middle-circle base-form thick" cx="50%" cy="50%" r="50"></circle>
            <circle className="middle-circle base-form medium" cx="50%" cy="50%" r="50"></circle>
            <circle className="middle-circle base-form thin" cx="50%" cy="50%" r="50"></circle>
            <circle className="middle-circle base-form thick-rev" cx="50%" cy="50%" r="50"></circle>
            <circle className="middle-circle base-form medium-rev" cx="50%" cy="50%" r="50"></circle>
            <circle className="middle-circle base-form thin-rev" cx="50%" cy="50%" r="50"></circle>
            <circle className="inner-circle base-form thick" cx="50%" cy="50%" r="40"></circle>
            <circle className="inner-circle base-form medium" cx="50%" cy="50%" r="40"></circle>
            <circle className="inner-circle base-form thin" cx="50%" cy="50%" r="40"></circle>
            <circle className="inner-circle base-form thick-rev" cx="50%" cy="50%" r="40"></circle>
            <circle className="inner-circle base-form medium-rev" cx="50%" cy="50%" r="40"></circle>
            <circle className="inner-circle base-form thin-rev" cx="50%" cy="50%" r="40"></circle>
          </svg>
        </div>
      </div>
      {messageComponent}
    </div>
  )
}
